import { useEffect, useRef, useState } from "react";
import type { Account, ExpenseInput, Owner, TrackedItemInput } from "../../types";
import { checkedTrackedItems } from "../../lib/tracked-items";
import { enqueuePendingEntry } from "../../lib/pending-entries";
import { syncPendingEntries } from "../../lib/sync-pending-entries";
import type { useLoanEntry } from "./use-loan-entry";
import type { useRecurringEntry } from "./use-recurring-entry";

/**
 * Persist each entry independently before dispatch. Pending entries never occupy the next form.
 */
export function useEntrySave({ profile, input, trackedItems, account, loan, recurring, onSaved }: {
  profile: Owner;
  input: ExpenseInput;
  trackedItems: TrackedItemInput[];
  account: Account | undefined;
  loan: ReturnType<typeof useLoanEntry>;
  recurring: ReturnType<typeof useRecurringEntry>;
  onSaved: (accountIds?: string[]) => void;
}) {
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [savedOnDevice, setSavedOnDevice] = useState(false);
  async function saveEntry() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const tracked = checkedTrackedItems(trackedItems);
      const options = recurring.fields;
      const key = enqueuePendingEntry({
        ...input, trackedItems: tracked, owner: profile,
        recurring: options.canMakeRecurring && options.makeRecurring ? {
          requestId: crypto.randomUUID(), type: input.type === "Income" ? "Income" : "Expense",
          name: input.title || input.category, accountId: input.accountId,
          payeeId: input.payeeId, category: input.category, subcategory: input.subcategory,
          amount: input.amount, currency: account?.currency || "MVR",
          frequency: options.frequency, reminderDaysBefore: options.reminderDaysBefore,
        } : undefined,
      });
      if (navigator.onLine) {
        try { await syncPendingEntries(); } catch { /* The durable queue remains available for retry. */ }
      }
      const pending = !!localStorage.getItem(key);
      // Offline saves finish immediately; online saves first attempt server confirmation.
      setSavedOnDevice(pending);
      setSaved(true);
      if (mounted.current && !pending)
        onSaved([input.accountId, ...(input.toAccountId ? [input.toAccountId] : [])]);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Could not keep the save reference. Please retry.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function saveLoanEntry() {
    if (busy) return;
    const missing = loan.missing();
    if (missing) {
      setError(missing);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { queued } = await loan.save();
      setSavedOnDevice(queued);
      setSaved(true);
      if (!queued) onSaved([input.accountId]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save. Please try again.");
    } finally {
      setBusy(false);
    }
  }
  return { locked: busy, busy, error, setError, saved, savedOnDevice, saveEntry, saveLoanEntry };
}
